import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFInput from "@/components/form/RHFInput";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

// Same source AddEditModel.jsx previews a tenant's own subdomain against —
// the root domain the platform is served from.
const DOMAIN_BASE = import.meta.env.VITE_DOMAIN_BASE;

const INPUT_STYLE =
    "w-full rounded-lg px-4 py-2.5 text-sm placeholder:text-text-muted bg-background-form/40 focus:outline-none transition text-text-primary";

// Same rule the tenant-user form applies to names (admin/user/validation.schema.js):
// letters, with single separators between them. Without it the field took
// "@#$%" and "12345" without a murmur.
const NAME_REGEX = /^[A-Za-z]+(?:[ .'-][A-Za-z]+)*$/;

// Email-only invite. AD SuperAdmins are provisioned via the promote flow
// (tenant admin promotes an existing AD user via /admin/super-admins).
const inviteSchema = z.object({
    name: z
        .string()
        .trim()
        .min(2, "Name must be at least 2 characters")
        .max(150, "Name must not exceed 150 characters")
        .regex(
            NAME_REGEX,
            "Name can only contain letters, spaces, apostrophes ('), hyphens (-), and periods (.)"
        ),
    email: z
        .string()
        .trim()
        .max(254, "Email must be 254 characters or fewer")
        .min(1, "Email is required")
        .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "Invalid email address"),
});

const InviteSuperAdminModal = ({ onClose, onInvited }) => {
    const methods = useForm({
        resolver: zodResolver(inviteSchema),
        mode: "onChange",
        defaultValues: { name: "", email: "" },
    });
    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = async (values) => {
        try {
            const res = await PlatformService.inviteSuperAdmin({
                name: values.name,
                email: values.email,
            });
            if (res?.success) {
                toast.success(`Invitation sent to ${values.email}`);
                onInvited?.();
            } else {
                toast.error("Failed to invite SuperAdmin");
            }
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[560px] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        Invite SuperAdmin
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                        The invitee receives an email with an accept-invite link that lands
                        on {`platform.${DOMAIN_BASE}/accept-invite`}. Setting a password there
                        activates the account.
                    </DialogDescription>
                    <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
                        <X size={16} strokeWidth={3} />
                    </DialogClose>
                </DialogHeader>

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="px-5 py-3 space-y-4">
                            <RHFInput
                                name="name"
                                label="Full name"
                                placeholder="e.g. Alex Morgan"
                                inputClass={INPUT_STYLE}
                                required
                            />

                            <RHFInput
                                name="email"
                                label="Email"
                                type="email"
                                placeholder="e.g. alex@yourcompany.com"
                                inputClass={INPUT_STYLE}
                                required
                            />

                            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
                                <div className="font-medium">SuperAdmin access</div>
                                <div className="mt-0.5">
                                    SuperAdmins can manage tenants, provision dedicated
                                    databases, and apply RLS updates fleet-wide. Grant
                                    carefully. To make an existing AD user a SuperAdmin,
                                    use the promote flow at
                                    <code className="mx-1">/admin/super-admins</code>
                                    instead — no invite email is needed.
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                            <DialogClose
                                render={
                                    <Button variant="outline" type="button">
                                        Cancel
                                    </Button>
                                }
                            />
                            <Button
                                type="submit"
                                className="bg-button-secondary text-white"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Sending…" : "Send invitation"}
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default InviteSuperAdminModal;
